import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { TONE_CLASSES } from 'components/admin/audit/pillTone';
import type { JiraFix } from 'api/sectorAlignmentApi';
import { useDeskInfo } from 'hooks/useSectorAlignment';
import { sourceLabel } from './deskSelection';
import { JIRA_GRID, list, type Tagged } from './rows';
import TicketKeyLink from './TicketKeyLink';

const JiraRow = ({ fix }: { fix: Tagged<JiraFix> }) => {
  const { t } = useTranslation();
  const deskName = useDeskInfo()(fix.sd).name;
  const tickets = list(fix.tickets);
  const key = tickets[0]?.key;
  const noStore = fix.reason === 'no-store';
  const multiple = fix.reason === 'multiple-sectors';
  const source = noStore ? t('Jira') : sourceLabel(t, fix.source);
  const problem = noStore
    ? t('Ticket without a store')
    : multiple
      ? t('Store attached to several sectors ({{sectors}}): its {{count}} tickets are not realigned', { sectors: list(fix.sectors).join(', '), count: tickets.length })
      : t('Store without a sector: its {{count}} tickets are visible to no ECM', { count: tickets.length });

  return (
    <div role="row" className={cn(JIRA_GRID, 'h-[52px] border-b last:border-b-0')}>
      <span role="cell" className="min-w-0 truncate">
        {noStore && key ? (
          <TicketKeyLink issueKey={key} />
        ) : (
          <span className="text-[13.5px] font-medium" title={fix.store ?? undefined}>{fix.store || '—'}</span>
        )}
      </span>
      <span role="cell" className="truncate text-[13px] text-muted-foreground" title={deskName}>{deskName}</span>
      <span role="cell">
        <span className={cn('inline-flex h-[22px] items-center rounded-md px-2 text-xs font-medium', TONE_CLASSES.zinc)}>{source}</span>
      </span>
      <span role="cell" className="flex min-w-0 items-center gap-2 text-[13px]">
        {multiple && <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-amber-500" aria-hidden="true" />}
        <span className="truncate" title={problem}>{problem}</span>
      </span>
    </div>
  );
};

export default JiraRow;
